import { Box, CircularProgress, Modal, Stack, Tooltip, Typography } from "@mui/material";
import MDBadge from "components/MDBadge";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40%",
  maxHeight: "80%",
  bgcolor: "background.paper",
  borderRadius: "10px",
  overflow: "auto",
  boxShadow: 24,
  p: 4,
};

function ModalLabel({ active, onClose, dataIndex, ...rest }) {
  const labels = useSelector((state) => state.label.list);
  const [label, setLabel] = useState({});
  const [condition, setCondition] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setLabel(labels[dataIndex]);
    setCondition(labels[dataIndex]?.conditions);
  }, [dataIndex, labels]);

  useEffect(() => {
    setLoading(false);
  }, [label]);

  const discountStatus = {
    DELETED: "error",
    SCHEDULED: "warning",
    EXPIRED: "secondary",
    ACTIVE: "success",
  };

  const conditionApply = {
    variants: "Specific product",
    select_all: "All product",
    matching_and_variants: "Products matching conditions",
  };

  const stockContent = {
    in_stock: "In stock",
    out_of_stock: "Out of stock",
    low_stock: "Low stock",
    stock_range: "Stock Range",
    any: "No",
  };

  const inventoryContent = {
    any: "",
    no: "Not new product",
    yes: "New product",
    date_range: "Creation date range",
  };

  const discountContent = {
    any: "",
    yes: "Sale product",
    no: "Not sale product",
  };

  const compareContent = {
    equal: "=",
    greater_than: ">",
    less_than: "<",
  };

  const sectionContent = {
    product: "Product section",
    collection: "Collection section",
    include_section: "Only in selectors",
    ignore_section: "Never in selectors",
  };

  // `condition.section` lists the enabled rules; empty means no section filter at all.
  const sectionRules = Array.isArray(condition?.section) ? condition.section : [];

  // Đơn vị của giá trị discount, hiển thị kèm ngay trong "Discount type".
  const discountUnit = condition?.discount_by === "percentage" ? "%" : "$";

  return (
    <Modal open={active} onClose={onClose}>
      <Box sx={style}>
        {loading ? (
          <Box>
            <CircularProgress color="info" />
          </Box>
        ) : (
          <>
            <Typography>{`Label: ${label?.name}`} </Typography>
            <Box sx={{ marginTop: 2 }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1}>
                  <Typography variant="body2"> Position:</Typography>
                  {label?.customize_position ? (
                    <Typography variant="body2" color="tomato">
                      customize
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="tomato">
                      {label?.position}
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Typography variant="body2">Page:</Typography>
                  {label?.page?.map((item, index) => {
                    return (
                      <MDBadge
                        key={index}
                        badgeContent={item}
                        color="success"
                        variant="gradient"
                        size="sm"
                      />
                    );
                  })}
                </Stack>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body2">Section:</Typography>
                    {sectionRules.length === 0 ? (
                      <Typography variant="body2" color="tomato">
                        All sections
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="tomato">
                        {sectionRules.map((item) => sectionContent[item] ?? item).join(", ")}
                      </Typography>
                    )}
                  </Stack>
                  {sectionRules.includes("include_section") && condition?.include_sections && (
                    <Typography variant="body2" color="tomato">
                      {`Only in: ${condition.include_sections}`}
                    </Typography>
                  )}
                  {sectionRules.includes("ignore_section") && condition?.ignore_sections && (
                    <Typography variant="body2" color="tomato">
                      {`Never in: ${condition.ignore_sections}`}
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Typography variant="body2">Device:</Typography>
                  {label?.device?.map((item, index) => {
                    return (
                      <MDBadge
                        key={index}
                        badgeContent={item}
                        color="success"
                        variant="gradient"
                        size="sm"
                      />
                    );
                  })}
                </Stack>
                {label?.language?.length > 0 && (
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body2">Language:</Typography>
                    <Typography variant="body2" color="tomato">
                      {label?.language.toString()}
                    </Typography>
                  </Stack>
                )}
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body2">Condition:</Typography>
                    <Typography variant="body2" color="tomato">
                      {conditionApply[label?.conditions?.variant_apply]}
                    </Typography>
                    {label?.conditions?.variant_apply === "matching_and_variants" && (
                      <MDBadge
                        badgeContent={label?.conditions?.condition_logic}
                        color="success"
                        variant="gradient"
                        size="sm"
                      />
                    )}
                  </Stack>
                  {condition?.include_variants?.length > 0 && (
                    <Stack>
                      {condition?.include_variants?.map((item, index) => {
                        return (
                          <Stack direction="row" spacing={1} key={index}>
                            {item.variants?.length == 1 &&
                            item.variants[0].title == "Default Title" ? (
                              <Typography variant="body2" color="tomato">
                                {`- ${item.title}`}
                              </Typography>
                            ) : (
                              <Tooltip
                                title={
                                  <Stack spacing={0.5} alignItems="flex-start">
                                    {item.variants?.map((variant, vIndex) => (
                                      <Typography key={vIndex} variant="caption">
                                        • {variant.title}
                                      </Typography>
                                    ))}
                                  </Stack>
                                }
                                arrow
                                placement="right"
                                slotProps={{
                                  tooltip: {
                                    sx: {
                                      backgroundColor: "#FFFFFF",
                                      color: "#000000",
                                      boxShadow:
                                        "0 1px 3px rgba(0, 0, 0, .3), 0 4px 8px 3px rgba(0, 0, 0, .15)",
                                    },
                                  },
                                  arrow: {
                                    sx: {
                                      color: "#FFFFFF",
                                      filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.3))",
                                    },
                                  },
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="tomato"
                                  sx={{
                                    cursor: "pointer",
                                    "&:hover": { textDecoration: "underline" },
                                  }}
                                >
                                  {`- ${item.title}`}
                                </Typography>
                              </Tooltip>
                            )}
                          </Stack>
                        );
                      })}
                    </Stack>
                  )}
                </Stack>
                {condition?.collections?.length > 0 && (
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body2">Collections:</Typography>
                    <Typography variant="body2" color="tomato">
                      {condition?.collections.toString()}
                    </Typography>
                  </Stack>
                )}
                {condition?.exclude_collections?.length > 0 && (
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body2">Exclude collections:</Typography>
                    <Typography variant="body2" color="tomato">
                      {condition?.exclude_collections.toString()}
                    </Typography>
                  </Stack>
                )}
                {condition?.tags?.length > 0 && (
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body2">Tags:</Typography>
                    <Typography variant="body2" color="tomato">
                      {condition?.tags.toString()}
                    </Typography>
                  </Stack>
                )}
                {condition?.is_new !== "any" && (
                  <>
                    <Stack direction="row" spacing={1}>
                      <Typography variant="body2">New status:</Typography>
                      <Typography variant="body2" color="tomato">
                        {inventoryContent[condition?.is_new]}
                      </Typography>
                    </Stack>
                    {condition?.is_new === "yes" && (
                      <Stack direction="row" spacing={1}>
                        <Typography variant="body2">New day threshold:</Typography>
                        <Typography variant="body2" color="tomato">
                          {`${condition?.new_days_threshold}`}
                        </Typography>
                      </Stack>
                    )}
                    {condition?.is_new === "date_range" && (
                      <Stack direction="row" spacing={1}>
                        <Typography variant="body2">Creation date range:</Typography>
                        <Typography variant="body2" color="tomato">
                          {`${condition?.new_date_from} - ${condition?.new_date_to}`}
                        </Typography>
                      </Stack>
                    )}
                  </>
                )}
                {condition?.stock_status !== "any" && (
                  <>
                    <Stack direction="row" spacing={1}>
                      <Typography variant="body2">Inventory status:</Typography>
                      <Typography variant="body2" color="tomato">
                        {stockContent[condition?.stock_status]}
                      </Typography>
                    </Stack>
                    {condition?.stock_status === "low_stock" && (
                      <Stack direction="row" spacing={1}>
                        <Typography variant="body2">Low stock threshold:</Typography>
                        <Typography variant="body2" color="tomato">
                          {condition?.low_stock_threshold}
                        </Typography>
                      </Stack>
                    )}
                    {condition?.stock_status === "stock_range" && (
                      <Stack direction="row" spacing={1}>
                        <Typography variant="body2">Stock range:</Typography>
                        <Typography variant="body2" color="tomato">
                          {`${condition?.stock_from} - ${condition?.stock_to}`}
                        </Typography>
                      </Stack>
                    )}
                  </>
                )}
                {condition?.is_on_sale !== "any" && (
                  <>
                    <Stack direction="row" spacing={1}>
                      <Typography variant="body2">Discount status:</Typography>
                      <Typography variant="body2" color="tomato">
                        {discountContent[condition?.is_on_sale]}
                      </Typography>
                    </Stack>
                    {condition?.is_on_sale === "yes" && (
                      <Stack direction="row" spacing={1}>
                        <Typography variant="body2">Discount type:</Typography>
                        <Typography variant="body2" color="tomato">
                          {condition?.discount_type === "fixed"
                            ? `Fixed[${condition?.discount_fixed}${discountUnit}]`
                            : `Range [${condition?.discount_from}${discountUnit} - ${condition?.discount_to}${discountUnit}]`}
                        </Typography>
                      </Stack>
                    )}
                  </>
                )}
                {condition?.use_price && (
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body2">Price:</Typography>
                    <Typography variant="body2" color="tomato">
                      {`${condition?.from_price} - ${condition?.to_price}`}
                    </Typography>
                  </Stack>
                )}

                {(condition?.from_weight || condition?.to_weight) && (
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body2">Weight:</Typography>
                    <Typography variant="body2" color="tomato">
                      {`${condition?.from_weight} - ${condition?.to_weight}`}
                    </Typography>
                  </Stack>
                )}
                {condition?.exclude_tags?.length > 0 && (
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body2">Exclude tags:</Typography>
                    <Typography variant="body2" color="tomato">
                      {condition?.exclude_tags.toString()}
                    </Typography>
                  </Stack>
                )}
                {condition?.title?.length > 0 && (
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body2">Title:</Typography>
                    <Typography variant="body2" color="tomato">
                      {`${condition?.title_type} - ${condition?.title_operator} - "${condition?.title}"`}
                    </Typography>
                  </Stack>
                )}
                {condition?.exclude_variants?.length > 0 && (
                  <Stack>
                    <Typography variant="body2">Exclude variants:</Typography>
                    {condition?.exclude_variants?.map((item, index) => {
                      return (
                        <Typography variant="body2" color="tomato" key={index}>
                          {`- ${item.title}`}
                        </Typography>
                      );
                    })}
                  </Stack>
                )}
                {condition?.include_pages?.length > 0 && (
                  <Stack>
                    <Typography variant="body2">Specific page:</Typography>
                    {condition?.include_pages?.split(",").map((item, index) => {
                      return (
                        <Typography variant="body2" color="tomato" key={index}>
                          {`- ${item}`}
                        </Typography>
                      );
                    })}
                  </Stack>
                )}
                {condition?.review_app?.length > 0 && (
                  <Stack>
                    <Typography variant="body2">Integrate review app:</Typography>
                    <Stack direction="row" spacing={1}>
                      <Typography variant="body2" color="tomato">
                        {condition?.review_app}
                      </Typography>
                      {condition?.review_option_selected == "star_rating" && (
                        <MDBadge
                          badgeContent={`star rating ${
                            compareContent[condition?.review_condition]
                          } ${condition?.review_star_rating}`}
                          color="success"
                          variant="gradient"
                          size="sm"
                        />
                      )}
                      {condition?.review_option_selected == "num_review" && (
                        <MDBadge
                          badgeContent={`num review ${
                            compareContent[condition?.review_condition]
                          } ${condition?.review_num_review}`}
                          color="success"
                          variant="gradient"
                          size="sm"
                        />
                      )}
                    </Stack>
                  </Stack>
                )}
                {condition?.free_gift_app_handle && (
                  <Stack>
                    <Typography variant="body2">Bundle &amp; free gift apps:</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" color="tomato">
                        {condition?.free_gift_app || condition?.free_gift_app_handle}
                      </Typography>
                      {condition?.free_gift_offers?.map((item, index) => (
                        <MDBadge
                          key={index}
                          badgeContent={item.title || item.name || item}
                          color="success"
                          variant="gradient"
                          size="sm"
                        />
                      ))}
                    </Stack>
                  </Stack>
                )}
                {label?.metafields?.length > 0 && (
                  <Stack spacing={1}>
                    <Typography variant="body2">Metafield:</Typography>
                    {label?.metafields?.map((item, index) => {
                      return (
                        <Stack key={index} direction="row" spacing={1}>
                          <Typography variant="body2" color="tomato">
                            {`- ${item.name}`}
                          </Typography>
                          <MDBadge
                            badgeContent={item.condition == "matching" ? "Equal to" : "Exists"}
                            color="success"
                            variant="gradient"
                            size="sm"
                          />
                          {item.condition == "matching" && (
                            <>
                              {!item.value ||
                              (typeof item.value === "object" && !item.value?.value) ? (
                                <MDBadge
                                  badgeContent={"null"}
                                  color="success"
                                  variant="gradient"
                                  size="sm"
                                />
                              ) : (
                                <MDBadge
                                  badgeContent={
                                    typeof item.value === "object"
                                      ? `${item.value.value.toString()} ${item.value.unit}`
                                      : item.value.toString()
                                  }
                                  color="success"
                                  variant="gradient"
                                  size="sm"
                                />
                              )}
                            </>
                          )}
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
                {condition?.customer_status === "login" && (
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body2">Customer login:</Typography>
                    <Typography variant="body2" color="tomato">
                      {condition?.customer_login}
                    </Typography>
                  </Stack>
                )}
                {condition?.customer_status === "tags" && (
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body2">Customer tags:</Typography>
                    <Typography variant="body2" color="tomato">
                      {condition?.customer_tag_status}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      {condition?.customer_tags?.map((item, index) => {
                        return (
                          <MDBadge
                            key={index}
                            badgeContent={item}
                            color="success"
                            variant="gradient"
                            size="sm"
                          />
                        );
                      })}
                    </Stack>
                  </Stack>
                )}
                {condition?.country_status && (
                  <Stack direction="row" spacing={2}>
                    <Stack direction="row" spacing={1}>
                      <Typography variant="body2">Customer country:</Typography>
                      <MDBadge
                        badgeContent={condition.country_status}
                        color="success"
                        variant="gradient"
                        size="sm"
                      />
                    </Stack>
                    <Typography variant="body2" color="tomato">
                      {condition?.countries?.toString().toUpperCase()}
                    </Typography>
                  </Stack>
                )}
                {condition?.hasOwnProperty("discount_id") &&
                  condition?.discount_id &&
                  label?.hasOwnProperty("discount") && (
                    <Stack spacing={1}>
                      <Typography variant="body2">Shopify Discounts:</Typography>
                      <Stack>
                        <Stack direction="row" spacing={1}>
                          <Tooltip
                            title={
                              <Stack spacing={0.5} alignItems="flex-start">
                                {Object.entries(label?.discount || {}).map(([key, value]) => (
                                  <Typography
                                    key={key}
                                    variant="caption"
                                    sx={{ whiteSpace: "nowrap" }}
                                  >
                                    <strong>{key}:</strong>{" "}
                                    {Array.isArray(value) ? value.join(", ") : value ?? "null"}
                                  </Typography>
                                ))}
                              </Stack>
                            }
                            arrow
                            placement="right"
                            slotProps={{
                              tooltip: {
                                sx: {
                                  backgroundColor: "#FFFFFF",
                                  color: "#000000",
                                  maxWidth: 400,
                                  boxShadow:
                                    "0 1px 3px rgba(0,0,0,.3), 0 4px 8px 3px rgba(0,0,0,.15)",
                                },
                              },
                              arrow: {
                                sx: {
                                  color: "#FFFFFF",
                                  filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.3))",
                                },
                              },
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="tomato"
                              sx={{
                                cursor: "pointer",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              {label?.discount?.title}
                            </Typography>
                          </Tooltip>
                          <MDBadge
                            badgeContent={label?.discount?.status}
                            color={
                              label?.discount?.status
                                ? discountStatus[label?.discount?.status]
                                : "success"
                            }
                            variant="gradient"
                            size="sm"
                          />
                        </Stack>
                        <Typography variant="body2" color="tomato">
                          (Summary: {label?.discount?.summary})
                        </Typography>
                      </Stack>
                    </Stack>
                  )}
                {condition?.deco_discount_id && label?.deco_discount && (
                  <Stack spacing={1}>
                    <Typography variant="body2">DECO discount:</Typography>
                    <Stack>
                      <Stack direction="row" spacing={1}>
                        <Typography variant="body2" color="tomato">
                          {label?.deco_discount?.title}
                        </Typography>
                        <MDBadge
                          badgeContent={
                            label?.deco_discount?.is_deleted
                              ? "DELETED"
                              : label?.deco_discount?.status
                              ? "ACTIVE"
                              : "INACTIVE"
                          }
                          color={
                            label?.deco_discount?.is_deleted
                              ? "error"
                              : label?.deco_discount?.status
                              ? "success"
                              : "secondary"
                          }
                          variant="gradient"
                          size="sm"
                        />
                      </Stack>
                      <Typography variant="body2" color="tomato">
                        (
                        {label?.deco_discount?.discount_apply === "percentage"
                          ? `${parseFloat(label?.deco_discount?.discount_value)}%`
                          : label?.deco_discount?.discount_value}{" "}
                        - {label?.deco_discount?.discount_type})
                      </Typography>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}

ModalLabel.defaultProps = {
  active: true,
  onClose: () => {},
  dataIndex: 0,
};

ModalLabel.propTypes = {
  active: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dataIndex: PropTypes.number, // Add this line if you plan to use the 'data' prop
};

export default ModalLabel;
